'use client';

import React, { useMemo } from 'react';
import { useSession } from './SessionProvider';

// ─── Gamma Sub-band Definitions ───────────────────────────────────────────────
// Low Gamma (30–60 Hz): sensory binding, working memory
// High Gamma (60–100 Hz): conscious perception, neural synchrony
// 40 Hz (MIT GENUS): targeted in Alzheimer's research — ADAS-Cog improvements
//   in clinical trials using 40 Hz sensory stimulation (Iaccarino et al., Nature 2016)

interface GammaSubBand {
  label: string;
  range: string;
  hz: string;
  description: string;
  color: string;
  glow: string;
  value: number; // 0–1 normalised
  insight: string;
}

function getGammaInterpretation(gammaPower: number): {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  description: string;
  clinicalNote: string;
} {
  // gammaPower is 0–1 normalised from session metrics
  if (gammaPower >= 0.72) return {
    label: 'High Gamma',
    color: '#a78bfa',
    bgColor: 'rgba(167,139,250,0.08)',
    borderColor: 'rgba(167,139,250,0.3)',
    description: 'Strong neural binding — peak conscious processing',
    clinicalNote: 'Elevated gamma correlates with heightened attention and sensory integration',
  };
  if (gammaPower >= 0.50) return {
    label: 'Active Gamma',
    color: '#38bdf8',
    bgColor: 'rgba(56,189,248,0.08)',
    borderColor: 'rgba(56,189,248,0.3)',
    description: 'Healthy gamma synchrony — normal cognitive engagement',
    clinicalNote: '40 Hz gamma is a key biomarker for cortical network integrity',
  };
  if (gammaPower >= 0.30) return {
    label: 'Reduced Gamma',
    color: '#f59e0b',
    bgColor: 'rgba(245,158,11,0.08)',
    borderColor: 'rgba(245,158,11,0.3)',
    description: 'Below-average gamma — possible fatigue or reduced engagement',
    clinicalNote: 'Reduced 40 Hz power observed in early MCI and Alzheimer\'s patients',
  };
  return {
    label: 'Suppressed Gamma',
    color: '#ef4444',
    bgColor: 'rgba(239,68,68,0.08)',
    borderColor: 'rgba(239,68,68,0.3)',
    description: 'Significantly low gamma — neural synchrony impaired',
    clinicalNote: 'Gamma suppression is a hallmark of neurodegeneration (Iaccarino et al., 2016)',
  };
}

// ─── Animated Gamma Waveform ──────────────────────────────────────────────────
function GammaWaveform({ power, color }: { power: number; color: string }) {
  const points = useMemo(() => {
    const pts: string[] = [];
    const W = 200;
    const H = 32;
    const amp = 6 + power * 10;
    const freq = 40; // 40 Hz visual metaphor
    for (let x = 0; x <= W; x += 2) {
      const y = H / 2 + Math.sin((x / W) * Math.PI * 2 * (freq / 10)) * amp
        + Math.sin((x / W) * Math.PI * 2 * (freq / 6)) * (amp * 0.4);
      pts.push(`${x},${y}`);
    }
    return pts.join(' ');
  }, [power]);

  return (
    <svg width="200" height="32" viewBox="0 0 200 32" className="opacity-80">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
      >
        <animate
          attributeName="stroke-dasharray"
          values="0 600;600 0;600 0"
          dur="1.8s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="stroke-dashoffset"
          values="600;0;-600"
          dur="1.8s"
          repeatCount="indefinite"
        />
      </polyline>
    </svg>
  );
}

// ─── Radial Power Gauge ───────────────────────────────────────────────────────
function GammaPowerGauge({ value, color }: { value: number; color: string }) {
  const R = 52;
  const cx = 64;
  const cy = 64;
  const startAngle = -210;
  const sweepAngle = 240;
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const describeArc = (start: number, end: number) => {
    const s = toRad(start);
    const e = toRad(end);
    const x1 = cx + R * Math.cos(s);
    const y1 = cy + R * Math.sin(s);
    const x2 = cx + R * Math.cos(e);
    const y2 = cy + R * Math.sin(e);
    const large = end - start > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2}`;
  };

  const filledEnd = startAngle + sweepAngle * value;
  const pct = Math.round(value * 100);

  return (
    <svg width="128" height="128" viewBox="0 0 128 128">
      {/* Track */}
      <path
        d={describeArc(startAngle, startAngle + sweepAngle)}
        fill="none"
        stroke="rgba(30,58,95,0.5)"
        strokeWidth="8"
        strokeLinecap="round"
      />
      {/* Fill */}
      <path
        d={describeArc(startAngle, filledEnd)}
        fill="none"
        stroke={color}
        strokeWidth="8"
        strokeLinecap="round"
        style={{ filter: `drop-shadow(0 0 6px ${color})` }}
      />
      {/* Glow ring */}
      <circle cx={cx} cy={cy} r={R - 14} fill="none" stroke={color} strokeWidth="1" strokeOpacity="0.12" />
      {/* Center value */}
      <text x={cx} y={cy - 6} textAnchor="middle" fill={color} fontSize="20" fontWeight="700" fontFamily="monospace">
        {pct}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" fill={color} fontSize="9" fontFamily="monospace" opacity="0.7">
        %
      </text>
      <text x={cx} y={cy + 24} textAnchor="middle" fill="rgba(148,163,184,0.7)" fontSize="8" fontFamily="monospace">
        GAMMA
      </text>
    </svg>
  );
}

// ─── Sub-band Bar ─────────────────────────────────────────────────────────────
function SubBandBar({ band }: { band: GammaSubBand }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold" style={{ color: band.color }}>{band.label}</span>
          <span className="text-[10px] text-zinc-500">{band.range}</span>
        </div>
        <span className="text-[11px] font-mono" style={{ color: band.color }}>{Math.round(band.value * 100)}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#0d1f35] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${band.value * 100}%`,
            background: `linear-gradient(90deg, ${band.color}88, ${band.color})`,
            boxShadow: `0 0 6px ${band.glow}`,
          }}
        />
      </div>
      <p className="text-[10px] text-zinc-500 leading-tight">{band.insight}</p>
    </div>
  );
}

// ─── MIT 40Hz Research Card ───────────────────────────────────────────────────
function MITResearchCard({ gammaPower }: { gammaPower: number }) {
  const hz40Power = Math.min(1, gammaPower * 1.05 + 0.02); // 40 Hz estimate
  const pct = Math.round(hz40Power * 100);
  const isHealthy = hz40Power >= 0.5;

  return (
    <div
      className="rounded-xl p-4 border"
      style={{
        background: 'linear-gradient(135deg, rgba(167,139,250,0.06) 0%, rgba(11,22,40,0.8) 100%)',
        borderColor: 'rgba(167,139,250,0.2)',
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.25)' }}
        >
          <span className="text-[15px]">🔬</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[12px] font-bold text-[#a78bfa]">MIT 40 Hz GENUS Research</span>
            <span
              className="text-[9px] font-mono px-1.5 py-0.5 rounded"
              style={{
                background: isHealthy ? 'rgba(52,211,153,0.12)' : 'rgba(245,158,11,0.12)',
                color: isHealthy ? '#34d399' : '#f59e0b',
                border: `1px solid ${isHealthy ? 'rgba(52,211,153,0.25)' : 'rgba(245,158,11,0.25)'}`,
              }}
            >
              {pct}% 40Hz
            </span>
          </div>
          <p className="text-[11px] text-zinc-400 leading-relaxed">
            Gamma Entrainment Using Sensory Stimuli (GENUS) — Iaccarino et al., <em>Nature</em> 2016.
            40 Hz flickering light/sound drives gamma oscillations, reducing amyloid-β and tau in mouse models.
            Human trials show ADAS-Cog improvements with daily 40 Hz stimulation.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 h-1 rounded-full bg-[#0d1f35] overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${pct}%`,
                  background: 'linear-gradient(90deg, #7c3aed, #a78bfa)',
                  boxShadow: '0 0 6px rgba(167,139,250,0.5)',
                }}
              />
            </div>
            <span className="text-[10px] text-[#a78bfa] font-mono">{pct}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function GammaBandPanel() {
  const { metrics } = useSession();
  const { gammaPower, betaPower, alphaPower } = metrics;

  const interp = getGammaInterpretation(gammaPower);

  // Derive sub-band estimates from available metrics
  const subBands: GammaSubBand[] = useMemo(() => [
    {
      label: 'Low Gamma',
      range: '30–60 Hz',
      hz: '~40 Hz',
      description: 'Sensory binding, working memory',
      color: '#38bdf8',
      glow: 'rgba(56,189,248,0.5)',
      value: Math.min(1, gammaPower * 0.9 + betaPower * 0.1),
      insight: 'Peaks during active memory encoding and sensory integration tasks',
    },
    {
      label: '40 Hz Band',
      range: '38–42 Hz',
      hz: '40 Hz',
      description: 'MIT GENUS target frequency',
      color: '#a78bfa',
      glow: 'rgba(167,139,250,0.5)',
      value: Math.min(1, gammaPower * 1.05 + 0.02),
      insight: 'Key Alzheimer\'s biomarker — reduced in MCI; target of GENUS therapy',
    },
    {
      label: 'High Gamma',
      range: '60–100 Hz',
      hz: '~80 Hz',
      description: 'Conscious perception, neural synchrony',
      color: '#f472b6',
      glow: 'rgba(244,114,182,0.5)',
      value: Math.min(1, gammaPower * 0.75 - alphaPower * 0.05 + 0.1),
      insight: 'Associated with conscious awareness and cross-regional neural communication',
    },
  ], [gammaPower, betaPower, alphaPower]);

  return (
    <div className="rounded-2xl overflow-hidden" style={{
      background: 'linear-gradient(160deg, rgba(11,22,40,0.95) 0%, rgba(6,13,26,0.98) 100%)',
      border: '1px solid rgba(30,58,95,0.5)',
    }}>
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-[#1E3A5F]/40">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.25)' }}
            >
              <span className="text-[16px]">⚡</span>
            </div>
            <div>
              <h3 className="text-[14px] font-bold text-white">Gamma Band Analysis</h3>
              <p className="text-[11px] text-zinc-500">30–100 Hz · Neural Binding & Consciousness</p>
            </div>
          </div>
          {/* Status badge */}
          <div
            className="px-3 py-1.5 rounded-lg text-[11px] font-semibold"
            style={{
              background: interp.bgColor,
              border: `1px solid ${interp.borderColor}`,
              color: interp.color,
            }}
          >
            {interp.label}
          </div>
        </div>
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Gauge + waveform */}
        <div className="flex flex-col items-center gap-3">
          <GammaPowerGauge value={gammaPower} color={interp.color} />
          <GammaWaveform power={gammaPower} color={interp.color} />
          <div
            className="w-full rounded-xl p-3 text-center"
            style={{ background: interp.bgColor, border: `1px solid ${interp.borderColor}` }}
          >
            <p className="text-[11px] font-semibold" style={{ color: interp.color }}>{interp.description}</p>
            <p className="text-[10px] text-zinc-500 mt-1 leading-tight">{interp.clinicalNote}</p>
          </div>
        </div>

        {/* Middle: Sub-band breakdown */}
        <div className="flex flex-col gap-4">
          <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">Sub-band Breakdown</p>
          {subBands.map(band => (
            <SubBandBar key={band.label} band={band} />
          ))}

          {/* Neuroscience context */}
          <div
            className="rounded-xl p-3 mt-1"
            style={{ background: 'rgba(30,58,95,0.2)', border: '1px solid rgba(30,58,95,0.4)' }}
          >
            <p className="text-[10px] text-zinc-400 leading-relaxed">
              <span className="text-[#38bdf8] font-semibold">Gamma oscillations</span> synchronise distributed neural networks,
              enabling feature binding and conscious perception. They are generated by fast-spiking
              parvalbumin interneurons and are among the first signals disrupted in Alzheimer's disease.
            </p>
          </div>
        </div>

        {/* Right: MIT research + context metrics */}
        <div className="flex flex-col gap-4">
          <MITResearchCard gammaPower={gammaPower} />

          {/* Gamma vs other bands context */}
          <div
            className="rounded-xl p-4"
            style={{ background: 'rgba(11,22,40,0.6)', border: '1px solid rgba(30,58,95,0.4)' }}
          >
            <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-3">Band Context</p>
            <div className="space-y-2">
              {[
                { label: 'Gamma / Beta ratio', value: betaPower > 0 ? (gammaPower / betaPower).toFixed(2) : '—', note: 'Binding vs. active thinking', color: '#a78bfa' },
                { label: 'Gamma / Alpha ratio', value: alphaPower > 0 ? (gammaPower / alphaPower).toFixed(2) : '—', note: 'Engagement vs. idle cortex', color: '#38bdf8' },
                { label: 'Raw gamma power', value: `${Math.round(gammaPower * 100)}%`, note: 'Normalised 0–100%', color: interp.color },
              ].map(row => (
                <div key={row.label} className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] text-zinc-300">{row.label}</p>
                    <p className="text-[10px] text-zinc-600">{row.note}</p>
                  </div>
                  <span className="text-[13px] font-mono font-bold" style={{ color: row.color }}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Key insight */}
          <div
            className="rounded-xl p-3"
            style={{ background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.15)' }}
          >
            <p className="text-[10px] text-zinc-400 leading-relaxed">
              <span className="text-[#a78bfa] font-semibold">Clinical relevance:</span>{' '}
              Gamma power reduction precedes cognitive decline by years. Regular monitoring of 40 Hz
              band integrity alongside theta/alpha slowing provides a multi-modal Alzheimer's risk profile.
            </p>
          </div>
        </div>
      </div>

      {/* Footer disclaimer */}
      <div className="px-5 pb-4">
        <p className="text-[10px] text-zinc-600 text-center">
          ⚠ Research context only — not a clinical diagnostic tool. References: Iaccarino et al. (2016) <em>Nature</em>; Adaikkan et al. (2019) <em>Neuron</em>.
        </p>
      </div>
    </div>
  );
}
